package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initMetroForComposeMultiplatformPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/MetroForComposeMultiplatform.md", FrontMatterElement.Builder { addScalar("title", "I liked Metro, so I went and read its sources"); addScalar("searchTitle", "Windows Phone Metro components for Compose Multiplatform"); addScalar("date", "2026-08-30"); addScalar("description", "How kvadrant-ui got built: a design language I missed, the Microsoft files that turned out to still be readable, and the one effect I built from a name instead of a document."); addScalar("library", "kvadrant-ui"); }))
}

@Page("/blog/metro-for-compose-multiplatform")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun MetroForComposeMultiplatformPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I liked Windows Phone. Not ironically — the flat type, the black that was actually black, the way a list ran off the edge of the screen instead of politely stopping at a margin. The phones are gone and the language went with them, and every so often I wanted it back in something I was building.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So I decided to port it to Compose Multiplatform. That is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kvadrant-ui") {
            org.jetbrains.compose.web.dom.Text("kvadrant-ui")
        }
        org.jetbrains.compose.web.dom.Text(": fifty-one composables, a core that depends on no Material artefact, and an optional adapter for applications that also use Material 3.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/start.png", "The Start screen: live tiles that flip, cycle and carry a badge")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-first-decision-was-where-to-look") }) {
        org.jetbrains.compose.web.dom.Text("The first decision was where to look")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every Metro reimplementation I could find was built the same way: from memory and screenshots. That is the obvious way to do it, and I was about to do the same, because what else is there — the SDK is a decade dead.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Except it isn't, quite. The Windows Phone SDK shipped a design-time ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("System.Windows.xaml")
        }
        org.jetbrains.compose.web.dom.Text(", the control templates are plain XAML inside the platform assembly, and the design guidelines are still readable. Once I started reading those instead of squinting at pictures, three things turned out to be backwards in my own memory of the phone:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a text box is a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("light")
            }
            org.jetbrains.compose.web.dom.Text(" field in both themes — not a dark one in the dark theme;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("focusing it does ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("not")
            }
            org.jetbrains.compose.web.dom.Text(" bring in the accent colour;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a button's visible border sits inside twelve pixels of invisible touch target.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("None of those show up in a screenshot. All three are things a reimplementation gets wrong and then defends, because it looks right.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That settled the method. Every colour, every metric and every motion curve in the library is generated from a vendored dump of Microsoft's own theme resources rather than typed in. Eight metrics have no public source anywhere — the Pivot's among them. Those ship as ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("parameters")
        }
        org.jetbrains.compose.web.dom.Text(" rather than constants, with a KDoc sentence saying the number is mine, so a caller who knows better can override it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("then-i-made-a-rule-that-made-the-whole-thing-harder") }) {
        org.jetbrains.compose.web.dom.Text("Then I made a rule that made the whole thing harder")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KvadrantTheme(remastered = true)")
        }
        org.jetbrains.compose.web.dom.Text(" is off by default. With it off, a component that looks ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("nicer")
        }
        org.jetbrains.compose.web.dom.Text(" than Windows Phone is a defect.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This sounds like asceticism and is really about being able to check the work. Without the rule you cannot tell a faithful component from a pleasant one by looking, and every \"small improvement\" goes in unreviewable. With it, each deviation is a row in the research document saying what it replaces. Restoring behaviour the original had is not a deviation and is not gated — that distinction is the whole value of the flag.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/controls.png", "Text boxes, a button, two toggle switches, list rows and tiles")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("and-then-i-broke-my-own-rule-without-noticing") }) {
        org.jetbrains.compose.web.dom.Text("And then I broke my own rule without noticing")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Windows Phone 7.1 added two visual state groups to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ScrollViewer")
        }
        org.jetbrains.compose.web.dom.Text(": ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("HorizontalCompression")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("VerticalCompression")
        }
        org.jetbrains.compose.web.dom.Text(". I read that name and built the effect from it. Content compressed towards the edge being pushed, boundary held still, nothing empty ever appearing.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It shipped. Then it got tuned three times on reports from a real phone — first the depth, then the shape, then the timing — and each round I defended with arithmetic. It never felt right, and I kept adjusting the numbers.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every one of those rounds was tuning the wrong effect. The design guidelines describe the behaviour in a sentence I had never gone looking for, because I already had a name:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("When the end of the list is reached, it will then ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("scroll up to display the empty section")
            }
            org.jetbrains.compose.web.dom.Text(" and \"rubber band\" back to rest in place.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An empty section is precisely what a squeeze cannot produce — a squeeze is ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("defined")
        }
        org.jetbrains.compose.web.dom.Text(" by the boundary not moving. \"Compression\" names the damping of the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("manipulation")
        }
        org.jetbrains.compose.web.dom.Text(", the scroll being resisted past its end, and says nothing about pixels. Which is also why those visual states carry no storyboard at all: they exist so an application can ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("notice")
        }
        org.jetbrains.compose.web.dom.Text(" the damping, not so it can draw it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Later I found the stronger version of the same answer in the platform assembly. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LongListSelector")
        }
        org.jetbrains.compose.web.dom.Text("'s control template is a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ViewportControl")
        }
        org.jetbrains.compose.web.dom.Text(" and a scrollbar, and that control's entire panning surface is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Bounds")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Viewport")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SetViewportOrigin")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BounceViewportToBounds")
        }
        org.jetbrains.compose.web.dom.Text(". A rectangle over the content plus an origin ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cannot express a deformation")
        }
        org.jetbrains.compose.web.dom.Text(". There is no scale anywhere in the manipulation path.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-tests-were-on-the-wrong-side") }) {
        org.jetbrains.compose.web.dom.Text("The tests were on the wrong side")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The part that actually stung: my test suite did not merely miss this. It had been written to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("exclude")
        }
        org.jetbrains.compose.web.dom.Text(" the correct behaviour, in as many words — it failed any implementation where \"the content is sliding away from the boundary rather than squeezing towards it\".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Green, specific, carefully argued, and enforcing the mistake for months.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A guard is worth exactly as much as the reading it was written from. And I did not find this myself either — somebody asked \"are you sure the content compressed rather than expanded?\" Doubt about the premise turned out to be worth more than another iteration inside it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("using-it") }) {
        org.jetbrains.compose.web.dom.Text("Using it")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories {
    maven("https://reposilite.kotlin.website/snapshots") {
        // Filtered, like every third-party repository should be: an unfiltered one takes part
        // in resolving everything.
        mavenContent { includeGroupAndSubgroups("io.github.youndie") }
    }
}

dependencies {
    implementation("io.github.youndie:kvadrant-core:0.1.0")
    // Only if the application also uses Material 3.
    implementation("io.github.youndie:kvadrant-material-adapter:0.1.0")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kvadrant-core")
        }
        org.jetbrains.compose.web.dom.Text(" publishes desktop (JVM), Android, wasm, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("iosArm64")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("iosSimulatorArm64")
        }
        org.jetbrains.compose.web.dom.Text("; the adapter publishes the first three and has no iOS variant.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then wrap the application once:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""KvadrantTheme(
    colors = KvadrantColors.dark(),
    typography = KvadrantTypography.default(kvadrantLatin()),
) {
    KvadrantPage(applicationTitle = "KVADRANT UI", pageTitle = "inbox") {
        KvadrantListItem("Anna Peterson", subtitle = "meeting on Thursday", onClick = {})
        KvadrantButton("reply", onClick = {})
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The press feedback is the theme's rather than the call site's: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KvadrantTheme")
        }
        org.jetbrains.compose.web.dom.Text(" replaces ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LocalIndication")
        }
        org.jetbrains.compose.web.dom.Text(" with the tilt, so anything clickable underneath it leans towards the finger without being asked to. It replaces ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LocalOverscrollFactory")
        }
        org.jetbrains.compose.web.dom.Text(" too — a Metro list neither ripples nor stretches at its end, and for a long time it did only the first of those, which is how the overscroll above came to be looked at in the first place.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("fonts-because-you-will-hit-this-immediately") }) {
        org.jetbrains.compose.web.dom.Text("Fonts, because you will hit this immediately")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No Segoe file is in the repository, and none can be. Selawik is Microsoft's own metric-compatible stand-in and is the reason a typeface ships here at all — it has no Cyrillic, so Source Sans 3 is bundled as the companion and text is routed ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("per character")
        }
        org.jetbrains.compose.web.dom.Text(". A ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FontFamily")
        }
        org.jetbrains.compose.web.dom.Text(" list selects weight and style variants; it does not fall back on a missing glyph, which I measured rather than assumed.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The forty icons are drawn for the same reason.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/icons.png", "Forty drawn icons")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is-not") }) {
        org.jetbrains.compose.web.dom.Text("What it is not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Only the SemiLight weight of the Cyrillic companion is calibrated against the render; the other four Metro weights are not. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("wasmJsBrowserTest")
        }
        org.jetbrains.compose.web.dom.Text(" is skipped, which in a green build reads exactly like a pass. And the tilt still gives each surface its own camera where the phone had one camera for the whole screen — the alternative is in the library, switchable in the demo, and off by default.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nobody has used this API yet, which is the honest reason the version is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0.1.0")
        }
        org.jetbrains.compose.web.dom.Text(" and not ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("1.0")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kvadrant-ui") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/kvadrant-ui")
        }
        org.jetbrains.compose.web.dom.Text(" · ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youndie.github.io/kvadrant-ui/") {
            org.jetbrains.compose.web.dom.Text("press the components")
        }
    }
}
